'use client';

import type { Product } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Paragraph } from '../ui/paragraph';
import { CarouselControls } from '../ui/carousel-controls';
import { Carousel } from '../ui/carousel';
import { ProductCard } from '@eliadmin/core/src/components/product/product-card';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { memo } from 'react';

export interface ProductsContainerViewProps {
  products: Product[];
  title: string;
  description?: string;
  variant: 'carousel' | 'grid';
  className?: { section?: string };
}

/** Presentation only: the caller retains the exact data/auth/cache owner. */
export function ProductsContainerView(props: ProductsContainerViewProps) {
  const carouselContext = useCarouselContext();
  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }
  return <ProductsContainerContent {...props} />;
}

// Context updates for carousel arrows/index must not rebuild every card.
// Individual cards still observe their own customer/filter/price state.
const ProductsContainerContent = memo(function ProductsContainerContent({
  products,
  title,
  description,
  variant,
  className,
}: ProductsContainerViewProps) {
  const options = {
    align: 'center' as const,
    loop: true,
  };

  if (variant === 'carousel') {
    return (
      <section className={cn('custom-section', 'pr-0', className?.section)}>
        <div
          className={cn(
            'custom-container',
            'flex flex-col items-start justify-start gap-6 sm:flex-row sm:items-center sm:justify-start md:gap-12 lg:gap-[140px]',
          )}
        >
          <div className="flex w-full flex-col items-start justify-start gap-8 sm:max-w-[332px]">
            <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4">
              <Heading title={title} />
              {!!description && <Paragraph className="w-full">{description}</Paragraph>}
            </div>
            <CarouselControls
              className={{
                main: 'hidden items-center justify-start gap-2 sm:flex',
              }}
            />
          </div>
          <div className="w-full" style={{ clipPath: 'inset(0 -100vw 0 0)' }}>
            <Carousel
              className={{
                container: 'w-full gap-4 sm:gap-5 lg:gap-6',
                main: 'w-full',
                wrapper: 'w-full',
                slide: 'w-full max-w-[300px] md:max-w-[332px]',
              }}
              slides={products.map(product => (
                <ProductCard product={product} key={product.slug} />
              ))}
            />
          </div>
        </div>
      </section>
    );
  }

  if (variant === 'grid') {
    return (
      <section className={cn('custom-section', 'pr-0 sm:px-5', 'section-gap', className?.section)}>
        <Heading center className={{ container: 'w-full max-w-[800px]' }} title={title} />
        {/* One set of cards, not a hidden second responsive copy with live subscriptions. */}
        <div className="custom-container w-full">
          <Carousel
            options={{
              ...options,
              active: false,
              breakpoints: { '(max-width: 639px)': { active: true } },
            }}
            className={{
              container:
                'w-full sm:grid sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:gap-6',
              main: 'w-full',
              wrapper: 'w-full',
              slide: 'mx-2 w-full max-w-[300px] sm:mx-0 sm:max-w-none',
            }}
            slides={products.map(product => (
              <ProductCard product={product} key={product.slug} />
            ))}
          />
        </div>
      </section>
    );
  }
});
